import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { showGlobalModal } from 'core/components/GlobalModal';
import { DigitalMarketingBreadcrumb } from 'features/DigitalMarketing/components/DigitalMarketingBreadcrumb';
import { ImagePreviewModal } from 'features/DigitalMarketing/components/ImagePreviewModal';
import { FieldLabel } from 'features/DigitalMarketing/components/FormControls';
import { DMFormField } from 'features/DigitalMarketing/components/DMFormField';
import { DMFileUploadDropzone } from 'features/DigitalMarketing/components/DMFileUploadDropzone';
import { DMImagePreviewWithActions } from 'features/DigitalMarketing/components/DMImagePreviewWithActions';
import { DMAiDescriptionBox } from 'features/DigitalMarketing/components/DMAiDescriptionBox';
import { DMQualityHint } from 'features/DigitalMarketing/components/DMQualityHint';
import { DMFormActions } from 'features/DigitalMarketing/components/DMFormActions';
import { DMDiv, DMText } from 'features/DigitalMarketing/components/Primitives';
import { DMSelectField } from 'features/DigitalMarketing/components/DMSelectField';
import { editWebsiteContentLabelClassName } from 'features/DigitalMarketing/constants/formStyles';
import {
  buildEditWebsiteContentPath,
  EDIT_WEBSITE_CONTENT_BASE_PATH,
} from 'features/DigitalMarketing/routes/editWebsiteContentPaths';
import { getApiErrorMessage } from 'features/DigitalMarketing/utils/apiResponse';
import {
  testimonialsApi,
  Testimonial,
} from 'features/DigitalMarketing/pages/EditWebsiteContent/api/testimonialsApi';
import { useAiDescription } from 'features/DigitalMarketing/pages/EditWebsiteContent/hooks/useAiDescription';
import { useWebsiteImageUpload } from 'features/DigitalMarketing/pages/EditWebsiteContent/hooks/useWebsiteImageUpload';
import { useImageEnhancer } from 'features/DigitalMarketing/hooks/useImageEnhancer';

type FormValues = {
  image: string;
  name: string;
  whoTheyAre: string;
  description: string;
  date: string;
  rating: string;
};

const emptyValues: FormValues = {
  image: '',
  name: '',
  whoTheyAre: '',
  description: '',
  date: '',
  rating: '',
};

const getInitialValues = (testimonial?: Testimonial): FormValues =>
  testimonial
    ? {
        image: testimonial.avatar,
        name: testimonial.clientName,
        whoTheyAre: testimonial.companyName,
        description: testimonial.feedback,
        date: testimonial.date,
        rating: String(testimonial.rating),
      }
    : emptyValues;

const getTestimonialPayload = (
  values: FormValues,
  testimonial?: Testimonial
) => ({
  PARENT_FEEDBACK_ID:
    testimonial?.parentFeedbackId || testimonial?.id || undefined,
  NAME: values.name,
  DESCRIPTION: values.description,
  RELATION: values.whoTheyAre,
  PARENT_FEEDBACK_RATING: Number(values.rating),
  PHOTO_URL: values.image,
});

export function TestimonialForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const testimonial = location.state?.testimonial as Testimonial | undefined;
  const isEditMode = location.pathname.endsWith('/edit');
  const title = `${isEditMode ? 'Edit' : 'Add'} Testimonial`;
  const [values, setValues] = React.useState<FormValues>(() =>
    getInitialValues(testimonial)
  );
  const [isSaving, setIsSaving] = React.useState(false);
  const [previewImageUrl, setPreviewImageUrl] = React.useState('');
  const { isUploadingImage, uploadImage } = useWebsiteImageUpload({
    preferObjectValue: isEditMode,
  });
  const { enhanceImage, isEnhancingImage } = useImageEnhancer();

  const handleEnhanceImage = async () => {
    const enhancedUrl = await enhanceImage(values.image);
    if (enhancedUrl) setValues(current => ({ ...current, image: enhancedUrl }));
  };
  const {
    isGeneratingDescription,
    descriptionGenerationsRemaining,
    improveDescription,
  } = useAiDescription({
    generateDescription: testimonialsApi.improveDescription,
    emptyMessage: 'Enter a testimonial description before improving it.',
  });

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = event.target;
    setValues(current => ({ ...current, [name]: value }));
  };

  const handleUploadImage = async (file: File) => {
    const localPreviewRef = { current: '' };
    const imageUrl = await uploadImage(file, localPreviewUrl => {
      localPreviewRef.current = localPreviewUrl;
      setValues(current => ({ ...current, image: localPreviewUrl }));
    });

    if (imageUrl) {
      setValues(current => ({ ...current, image: imageUrl }));
    } else if (localPreviewRef.current) {
      setValues(current =>
        current.image === localPreviewRef.current
          ? { ...current, image: '' }
          : current
      );
    }
  };

  const handleGenerateDescription = async () => {
    await improveDescription(values.description, description => {
      setValues(current => ({ ...current, description }));
    });
  };

  const showSuccessModal = () => {
    showGlobalModal(
      <>
        <DMText as="span" className="block font-manropeB text-[2.6vh]">
          {isEditMode
            ? 'All edits have been saved'
            : 'Testimonial has been added'}
        </DMText>
        <DMText as="span" className="mt-[1vh] block text-[1.8vh] text-gray-500">
          {isEditMode
            ? 'changes have been successfully published on the school website.'
            : 'testimonial has been successfully published on the school website.'}
        </DMText>
      </>
    );
    window.setTimeout(
      () => navigate(buildEditWebsiteContentPath('testimonials')),
      3000
    );
  };

  const saveTestimonial = async () => {
    setIsSaving(true);

    try {
      await testimonialsApi.upsert(
        getTestimonialPayload(values, isEditMode ? testimonial : undefined)
      );
      showSuccessModal();
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to save testimonial'),
        'error'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (
      !values.name ||
      !values.whoTheyAre ||
      !values.description ||
      !values.rating
    ) {
      showGlobalModal('Complete all fields before submitting', 'warning');
      return;
    }

    if (isEditMode && testimonial?.status === 'Live') {
      showGlobalModal(
        <>
          <DMText as="span" className="block font-manropeB text-[2.6vh]">
            Do you want to save changes to this testimonial?
          </DMText>
          <DMText
            as="span"
            className="mt-[1vh] block text-[1.8vh] text-gray-500"
          >
            Once you save changes, this testimonial will be updated on the
            school website.
          </DMText>
        </>,
        'warning',
        {
          showConfirm: true,
          onConfirm: saveTestimonial,
          onCancel: true,
          confimLabel: 'Yes',
          cancelLabel: 'Cancel',
        }
      );
      return;
    }

    await saveTestimonial();
  };

  return (
    <DMDiv
      as="main"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-white px-[2.5vw] py-[2.2vh] font-inter text-gray-950"
    >
      <DigitalMarketingBreadcrumb
        className="mb-[4vh] shrink-0"
        items={[
          {
            label: 'Edit Website Content',
            path: EDIT_WEBSITE_CONTENT_BASE_PATH,
          },
          {
            label: 'Testimonials',
            path: buildEditWebsiteContentPath('testimonials'),
          },
          { label: title },
        ]}
      />

      <DMDiv
        as="form"
        onSubmit={handleSubmit}
        className="flex min-h-0 flex-1 flex-col overflow-hidden"
      >
        <DMDiv className="grid shrink-0 grid-cols-2 gap-x-[1.8vw]">
          <DMDiv>
            <FieldLabel className={editWebsiteContentLabelClassName}>
              Image of the Person
            </FieldLabel>
            <DMDiv
              className={`flex h-[28vh] items-center justify-center rounded-[10px] border bg-white ${
                values.image
                  ? 'gap-[3.2vw] border-transparent'
                  : 'flex-col border-dashed border-gray-300 text-center'
              }`}
            >
              {values.image ? (
                <DMImagePreviewWithActions
                  imageUrl={values.image}
                  altText={values.name || 'Testimonial person'}
                  isUploading={isUploadingImage}
                  isEnhancing={isEnhancingImage}
                  onPreview={() => setPreviewImageUrl(values.image)}
                  onUpload={handleUploadImage}
                  onEnhance={handleEnhanceImage}
                  onRemove={() =>
                    setValues(current => ({ ...current, image: '' }))
                  }
                />
              ) : (
                <DMFileUploadDropzone
                  isUploading={isUploadingImage}
                  onFileSelect={handleUploadImage}
                  className="!border-none !bg-transparent h-full w-full"
                />
              )}
            </DMDiv>
            <DMQualityHint className="mt-[0.9vh]" />
          </DMDiv>

          <DMDiv>
            <DMAiDescriptionBox
              required
              value={values.description}
              onChange={handleInputChange}
              onGenerate={handleGenerateDescription}
              isGenerating={isGeneratingDescription}
              generationsRemaining={descriptionGenerationsRemaining}
              generateButtonLabel="Improve with AI"
              generatingLabel="Improving..."
            />
          </DMDiv>
        </DMDiv>

        <DMDiv className="mt-[1.85vh] grid shrink-0 grid-cols-2 gap-x-[1.8vw] gap-y-[1.2vh]">
          <DMFormField
            required
            label="Name"
            name="name"
            placeholder="Enter Name of the Person"
            value={values.name}
            onChange={handleInputChange}
          />
          {/* Date is not part of the testimonials upsert payload. */}
          <DMFormField
            required
            label="Who They Are"
            name="whoTheyAre"
            placeholder="Ex: Parent"
            value={values.whoTheyAre}
            onChange={handleInputChange}
          />
          <DMSelectField
            required
            label="Select Rating"
            name="rating"
            value={values.rating}
            placeholder="Select Rating"
            options={[1, 2, 3, 4, 5].map(r => ({
              label: String(r),
              value: String(r),
            }))}
            onChange={handleInputChange}
          />
        </DMDiv>

        <DMFormActions
          isSubmitting={isSaving}
          isDisabled={isUploadingImage || isEnhancingImage || isGeneratingDescription}
          submitLabel={isEditMode ? 'Save Changes' : 'Add Testimonial'}
          resetLabel={isEditMode ? 'Back' : 'Reset'}
          onReset={() =>
            isEditMode
              ? navigate(buildEditWebsiteContentPath('testimonials'))
              : setValues(emptyValues)
          }
          submitClassName="h-[6.6vh] w-[16vw] rounded-full bg-primary-main text-[1.85vh] font-semibold text-white disabled:opacity-60"
        />
        <ImagePreviewModal
          imageUrl={previewImageUrl}
          alt={values.name || 'Testimonial person'}
          onClose={() => setPreviewImageUrl('')}
        />
      </DMDiv>
    </DMDiv>
  );
}
